import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import type { ServiceContext } from "@prolarus/shared";
import { tryCreate, tryDelete, tryGet, tryList, tryUpdate, tryUpdatePost } from "./_shared";

export type PriceBook = Tables<"price_books">;
export type PriceBookListView = Tables<"vw_price_book_list">;
export type PriceBookInsert = Omit<TablesInsert<"price_books">, "company_id">;
export type PriceBookUpdate = TablesUpdate<"price_books">;

export interface StatusChangeOptions {
  comments?: string;
  dueToReassignment?: boolean;
}

export const priceBookService = {
  get(id: string) {
    return tryGet<PriceBook>(`/api/price-books/${encodeURIComponent(id)}`);
  },
  getWithRelations(id: string) {
    return tryGet<PriceBook & {
      vendor?: { name: string } | null;
      status?: { name: string } | null;
      region?: { display_value: string } | null;
      segment?: { display_value: string } | null;
    }>(`/api/price-books/${encodeURIComponent(id)}?withRelations=true`);
  },
  getWithAuditInfo(id: string) {
    return tryGet<PriceBook & {
      vendors?: { name: string } | null;
      list_regions?: { display_value: string } | null;
      list_segments?: { display_value: string } | null;
      created_by_profile?: { full_name: string | null } | null;
      updated_by_profile?: { full_name: string | null } | null;
    }>(`/api/price-books/${encodeURIComponent(id)}?withAudit=true`);
  },
  list(_companyId: string) {
    return tryList<PriceBook>("/api/price-books");
  },
  listForCards(_companyId: string) {
    return tryList<PriceBookListView>("/api/price-books?variant=cards");
  },
  listForSelector(_companyId: string) {
    return tryList<{
      id: string;
      name: string;
      vendor_id: string;
      status_id: string | null;
      effective_date: string;
      current_status: { id: string; name: string } | null;
      vendor: { name: string } | null;
    }>("/api/price-books?variant=selector");
  },
  listByVendor(vendorId: string) {
    return tryList<PriceBook>(`/api/price-books?vendorId=${encodeURIComponent(vendorId)}`);
  },
  listByVendorWithRelations(vendorId: string) {
    return tryList<PriceBook & {
      current_status: { id: string; name: string } | null;
      list_regions: { display_value: string } | null;
      list_segments: { display_value: string } | null;
    }>(`/api/price-books?vendorId=${encodeURIComponent(vendorId)}&variant=withRelations`);
  },
  getActiveForVendor(vendorId: string) {
    return tryGet<PriceBook>(`/api/price-books/active-for-vendor/${encodeURIComponent(vendorId)}`);
  },
  create(data: PriceBookInsert, _context: ServiceContext) {
    return tryCreate<PriceBook>("/api/price-books", data);
  },
  update(id: string, data: PriceBookUpdate, _snapshot: Record<string, unknown>, _context: ServiceContext) {
    return tryUpdate<PriceBook>(`/api/price-books/${encodeURIComponent(id)}`, data);
  },
  delete(id: string, _snapshot: Record<string, unknown>, _context: ServiceContext) {
    return tryDelete(`/api/price-books/${encodeURIComponent(id)}`);
  },
  updateStatus(id: string, newStatusId: string, previousStatusId: string | null, _context: ServiceContext, options?: StatusChangeOptions) {
    return tryUpdatePost<PriceBook>(`/api/price-books/${encodeURIComponent(id)}/status`, {
      status_id: newStatusId,
      previous_status_id: previousStatusId,
      comments: options?.comments,
      due_to_reassignment: options?.dueToReassignment,
    });
  },
};
